import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../redux/store';
import { setSlotEntry, type MealType } from '../../../redux/mealPlanSlice';
import { consumeLeftoverPortionAndSync } from '../../../redux/inventorySlice';
import { leftoverSubLabel, type LeftoverCandidate } from './leftovers';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Put one portion of a leftover into a slot, wherever it came from.
 *
 * Both pickers and the one-tap slot hints go through here so the two origins
 * cannot drift: a fridge portion is bookkeeping-free (remaining is derived from
 * the entries themselves), while a freezer portion has to decrement the pantry
 * row it was thawed from.
 */
export function useLeftoverPlacement(t: Translate) {
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((s: RootState) => s.auth?.user?.uid as string | undefined);

  return useCallback(
    (c: LeftoverCandidate, scheduledFor: string, mealType: MealType) => {
      dispatch(
        setSlotEntry({
          entry: {
            type: 'leftover',
            refRecipeId: c.refRecipeId,
            sub: leftoverSubLabel(c, t),
            portions: 1,
            ...(c.kind === 'fridge'
              ? { sourceMealId: c.sourceMealId }
              : { pantryItemId: c.pantryItemId, fromFreezer: true }),
          },
          scheduledFor,
          mealType,
        })
      );

      // The fridge needs no write: `buildFridgeCandidates` derives what is left
      // from the entries in the plan, so deleting this slot gives the portion
      // back on its own. The freezer is a real inventory row and must be told.
      if (c.kind === 'freezer' && c.pantryItemId) {
        dispatch(
          consumeLeftoverPortionAndSync({ id: c.pantryItemId, portions: 1, userId })
        );
      }
    },
    [dispatch, t, userId]
  );
}
